import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { loginRequest } from "../msalConfig";
import { useMsal } from "@azure/msal-react";
import userService from "../services/user/UserService";
import auth from "../services/auth/AuthService";
import UserService from "../services/user/UserService";
import { defaultLanguage, languages } from "../i18n";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface IUseSsoLoginProps {
  setIsAlert?: Dispatch<SetStateAction<boolean>>;
  setAlertMessage?: Dispatch<SetStateAction<string>>;
  shouldProcessRedirect?: boolean;
}

const useSsoLogin = ({ setIsAlert, setAlertMessage, shouldProcessRedirect = false }: IUseSsoLoginProps) => {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const onAcquireToken = async () => {
    if (!accounts.length) return;

    try {
      setIsLoading(true);

      const { idToken } = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      if (!idToken) return;

      const { guid } = await userService.MicrosoftLogin(idToken);
      await saveUser(guid);
    }
    catch (error) {
      await onSsoLogout();
      if (setIsAlert && setAlertMessage) {
        setIsAlert(true);
        setAlertMessage(t("login.accoutNotAuthorized"));
      }
    }
    finally {
      setIsLoading(false);
    }
  }

  const saveUser = async (guid: string) => {
    const user = await UserService.GetUserByGuid(guid);

    if (user) {
      auth.setAuth(user);
      const lang = languages.get(user.language) ?? languages.get(defaultLanguage);
      i18n.changeLanguage(lang);
      moment.locale(lang);
      window.location.href = "/";
    }
  };

  // Only the MSAL redirect target (login page) may finalize SSO sign-in — it ends in a hard reload to "/".
  // Running it from the always-mounted Sidebar re-triggers login + reload on every page (the SSO flash loop).
  useEffect(() => {
    if (!shouldProcessRedirect) return;

    onAcquireToken();
  }, [accounts, shouldProcessRedirect]);

  const onSsoLogin = async () => {
    await instance.loginRedirect({
      ...loginRequest,
      prompt: "select_account"
    });
  };

  const onSsoLogout = async () => {
    if (!accounts.length) return;

    await instance.logoutRedirect({
      account: accounts[0],
      onRedirectNavigate: () => false // Return false to stop navigation after local logout
    });
  };

  return {
    onSsoLogin,
    onSsoLogout,
    isLoading,
  };
};

export default useSsoLogin;
